import { WmButton } from "@watermarkinsights/ripple-react";
import { useContext } from "react";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import { CheckCircle, InstitutionIcon } from "@/features/common/Icons";
import WidgetLoader from "@/features/common/elements/WidgetLoader";
import {
  Product,
  ReportingYear
} from "@/features/common/interfaces/Interface";
import translate from "@/i18n/translate";
import "../../sll/Sll.scss";
import { ProgramInsights } from "./ProgramInsights";
import { ContributorInsights } from "./ContributorInsights";
import { useNavigate } from "react-router-dom";
import { scrollToTop } from "@/features/common/helper";

interface OrganizationProgramInsightsProps {
  currentReportingYear: ReportingYear | null;
}

export default function OrganizationProgramInsights({
  currentReportingYear
}: OrganizationProgramInsightsProps) {
  const { sysUser } = useContext<AppContextInterface>(AppContext);
  const isAdminUser = sysUser?.roles.includes("administrator");

  return (
    <>
      <div
        className={
          isAdminUser
            ? "view-insights-container"
            : "view-insights-container contributor-container"
        }
      >
        {isAdminUser && (
          <AdministratorInsights currentReportingYear={currentReportingYear} />
        )}
        {!isAdminUser && <ContributorInsights />}
      </div>
    </>
  );
}

function AdministratorInsights({
  currentReportingYear
}: OrganizationProgramInsightsProps) {
  const { institution } = useContext<AppContextInterface>(AppContext);
  return (
    <>
      <h2 className="heading">
        {translate("app.home.organizationProgramInsights")}
      </h2>
      <div className="desc">
        {translate("app.home.organizationProgramInsightsDesc")}
      </div>
      <div className="wm-grid">
        <div className="wm-grid-row">
          <div className="grid-container wm-grid-col-6 wm-grid-col-md-10 wm-grid-col-sm-8">
            <InstitutionInsights />
          </div>
          {institution && institution?.isSllSysAdminIntegrated && (
            <div className="grid-container wm-grid-col-6 wm-grid-col-md-10 wm-grid-col-sm-8">
              <ProgramInsights currentReportingYear={currentReportingYear} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function InstitutionInsights() {
  const { institution, integratedProducts, setBreadcrumb } =
    useContext<AppContextInterface>(AppContext);
  const navigate = useNavigate();

  const queryString = window.location.search;
  let institutionInsightsUrl = queryString.includes("locale")
    ? `/insights/institution/${institution?.platformUuid}/${queryString}`
    : `/insights/institution/${institution?.platformUuid}`;

  function navigateToInstitutionsInsights(url: string) {
    scrollToTop(".home-page-header");
    navigate(url);
    setBreadcrumb("home");
  }

  return (
    <div className="institution-insights-card paper-card">
      <div className="header-container header">
        <div>
          <div className="institution-name flex-row">
            <InstitutionIcon />
            <div className="">
              <WmButton
                buttonType="textonly"
                onClick={() =>
                  navigateToInstitutionsInsights(institutionInsightsUrl)
                }
              >
                <div className="instiution-link">{institution?.name}</div>
              </WmButton>
            </div>
          </div>
          <div className="institution-text">
            {translate("app.home.org.institution")}
          </div>
        </div>
        <WmButton
          buttonType="primary"
          onClick={() => navigateToInstitutionsInsights(institutionInsightsUrl)}
        >
          {translate("app.home.goToInstitution")}
        </WmButton>
      </div>
      <div className="body">
          <div className="product-list-heading">
            {translate("app.home.availableInsightsHeading")}
          </div>
          <div>
            {integratedProducts === null && (
              <WidgetLoader
                loaderContainerClasses="loader"
                loaderClasses="content"
              />
            )}
            {integratedProducts !== null &&
              integratedProducts.map(function (product: Product) {
                return (
                  <div key={`${product.id}`} className="product-list-item">
                    <CheckCircle />
                    <span>{product.name}</span>
                  </div>
                );
              })}
          </div>
        </div>
    </div>
  );
}
